// Phase 26C · Distributor Groups admin master.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Users, Tag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

const blankGroup = () => ({ code: "", name: "", description: "", is_active: true, secondary_price_list_id: "", primary_price_list_id: "" });

export default function DistributorGroupsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("masters.manage");
  const [rows, setRows] = useState([]);
  const [priceLists, setPriceLists] = useState({ SECONDARY: [], PRIMARY: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [assigning, setAssigning] = useState(null); // {group, distributors, selected:Set}
  const [distributors, setDistributors] = useState([]);
  const [assignFilter, setAssignFilter] = useState("");
  // Hotfix drop #4: multi-select "move to different group" affordance —
  // lets admin swap the dialog's target group without closing the dialog.
  const [changingTarget, setChangingTarget] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, s, p] = await Promise.all([
        api.get("/distributor-groups", { params: { page_size: 100 } }),
        api.get("/price-lists", { params: { list_type: "SECONDARY", is_active: true, page_size: 200 } }),
        api.get("/price-lists", { params: { list_type: "PRIMARY", is_active: true, page_size: 200 } }),
      ]);
      setRows(g.data.items || []);
      setPriceLists({ SECONDARY: s.data.items || [], PRIMARY: p.data.items || [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const g = editing.group;
      const body = { ...g };
      // convert empty strings → null for optional FKs
      body.secondary_price_list_id = g.secondary_price_list_id || null;
      body.primary_price_list_id = g.primary_price_list_id || null;
      if (editing.isNew) {
        await api.post("/distributor-groups", body);
        toast.success(`Group '${g.code}' created`);
      } else {
        const { code, ...patch } = body;
        await api.patch(`/distributor-groups/${g.id}`, patch);
        toast.success(`Group '${g.code}' updated`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/distributor-groups/${confirmDelete.id}`);
      toast.success(`Group '${confirmDelete.code}' deleted`);
      setConfirmDelete(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAssign = async (group) => {
    setAssigning({ group, selected: new Set() });
    setAssignFilter("");
    setChangingTarget(false);
    try {
      // Paginated fetch — the /api/distributors list route caps page_size at
      // le=200. A previous single-shot `page_size:500` fetch triggered a
      // pydantic 422 and left the assign dialog empty (see hotfix drop #4).
      // Mirror the pagination pattern used by frontend/src/offline/masters.js.
      const PAGE_SIZE = 200;
      const PAGE_CAP = 50;   // safety: up to 10k distributors
      const all = [];
      for (let page = 1; page <= PAGE_CAP; page += 1) {
        const { data } = await api.get("/distributors",
          { params: { page, page_size: PAGE_SIZE } });
        const items = data.items || [];
        all.push(...items);
        if (items.length < PAGE_SIZE) break;
      }
      setDistributors(all);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doAssign = async () => {
    const ids = Array.from(assigning.selected || []);
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/distributor-groups/${assigning.group.id}/assign`,
        { distributor_ids: ids });
      const already = ids.length - (data.moved || 0);
      const summary =
        `${data.moved} assigned` +
        (already > 0 ? `, ${already} already in target` : "");
      toast.success(`${summary} → ${assigning.group.code}`,
                     { id: "distgroup-move-toast" });
      setAssigning(null);
      setAssignFilter("");
      await load();
    } catch (e) { toast.error(formatApiError(e), { id: "distgroup-move-toast" }); }
  };

  return (
    <div className="space-y-5" data-testid="admin-distributor-groups-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Distributor Groups" }]}
        title="Distributor Groups"
        subtitle="Bundle distributors that share the same secondary + primary price lists."
        favorite={{ key: "admin.distributor-groups", type: "screen", label: "Distributor Groups", route: "/admin/distributor-groups" }}
        testid="distributor-groups-page-header"
        actions={canManage ? (
          <Button data-testid="dg-new" onClick={() => setEditing({ group: blankGroup(), isNew: true })}
            className="tulsi-gradient text-white h-10 font-semibold rounded-xl shadow-md shadow-[#E5197F]/20">
            <Plus className="h-4 w-4 mr-2" /> New group
          </Button>
        ) : null}
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Groups</CardTitle>
          <CardDescription>Every distributor lives in exactly one group. DEFAULT is auto-seeded and cannot be deleted.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex items-center gap-2 text-sm text-neutral-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            : rows.length === 0 ? <div className="text-sm text-neutral-500 py-4">No groups yet.</div>
            : (
              <Table data-testid="dg-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Secondary list</TableHead>
                    <TableHead>Primary list</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Distributors</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => {
                    const sec = priceLists.SECONDARY.find(x => x.id === r.secondary_price_list_id);
                    const pri = priceLists.PRIMARY.find(x => x.id === r.primary_price_list_id);
                    return (
                      <TableRow key={r.id} data-testid={`dg-row-${r.code}`}>
                        <TableCell className="font-mono text-xs">{r.code}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell>{sec ? <Badge variant="outline" className="text-xs">{sec.code}</Badge> : <span className="text-neutral-400 text-xs">—</span>}</TableCell>
                        <TableCell>{pri ? <Badge variant="outline" className="text-xs">{pri.code}</Badge> : <span className="text-neutral-400 text-xs">—</span>}</TableCell>
                        <TableCell>{r.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                        <TableCell className="text-right"><Badge variant="outline">{r.assigned_distributor_count}</Badge></TableCell>
                        <TableCell className="text-right">
                          {canManage && (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openAssign(r)}
                                data-testid={`dg-assign-${r.code}`} title="Assign distributors">
                                <Users className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditing({ group: { ...blankGroup(), ...r }, isNew: false })}
                                data-testid={`dg-edit-${r.code}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {r.code !== "DEFAULT" && (
                                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}
                                  data-testid={`dg-delete-${r.code}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="dg-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.isNew ? "Create distributor group" : `Edit — ${editing?.group.code}`}</DialogTitle>
            <DialogDescription>Attach a secondary + primary price list; every distributor in this group will resolve prices from those lists first.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={editing.group.code} onChange={(e) => setEditing({ ...editing, group: { ...editing.group, code: e.target.value.toUpperCase() } })}
                    disabled={!editing.isNew} placeholder="STRICT" data-testid="dg-code" />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input value={editing.group.name} onChange={(e) => setEditing({ ...editing, group: { ...editing.group, name: e.target.value } })}
                    placeholder="Strict distributor group" data-testid="dg-name" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={2} value={editing.group.description || ""}
                  onChange={(e) => setEditing({ ...editing, group: { ...editing.group, description: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Secondary price list</Label>
                  <Select value={editing.group.secondary_price_list_id || "none"}
                    onValueChange={(v) => setEditing({ ...editing, group: { ...editing.group, secondary_price_list_id: v === "none" ? "" : v } })}>
                    <SelectTrigger data-testid="dg-sec-list" className="h-10 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {priceLists.SECONDARY.map(p => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Primary price list</Label>
                  <Select value={editing.group.primary_price_list_id || "none"}
                    onValueChange={(v) => setEditing({ ...editing, group: { ...editing.group, primary_price_list_id: v === "none" ? "" : v } })}>
                    <SelectTrigger data-testid="dg-pri-list" className="h-10 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {priceLists.PRIMARY.map(p => <SelectItem key={p.id} value={p.id}>{p.code} · {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch id="dg-active-switch" checked={editing.group.is_active}
                  onCheckedChange={(v) => setEditing({ ...editing, group: { ...editing.group, is_active: v } })}
                  data-testid="dg-active" />
                <Label htmlFor="dg-active-switch">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="dg-save" className="tulsi-gradient text-white font-semibold">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing?.isNew ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assigning} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="max-w-2xl" data-testid="dg-assign-dialog">
          <DialogHeader>
            <DialogTitle>
              Assign distributors →{" "}
              {changingTarget ? (
                <Select
                  value={assigning?.group.id || ""}
                  onValueChange={(v) => {
                    const g = rows.find((x) => x.id === v);
                    if (g) setAssigning({ ...assigning, group: g });
                    setChangingTarget(false);
                  }}>
                  <SelectTrigger className="h-8 inline-flex w-auto min-w-[10rem]"
                                  data-testid="distgroup-move-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {rows.filter((g) => g.is_active !== false)
                          .map((g) => (
                      <SelectItem key={g.id} value={g.id}
                                    data-testid={`distgroup-move-target-opt-${g.code}`}>
                        {g.code} · {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <>
                  {assigning?.group.code}
                  {rows.length > 1 && (
                    <Button variant="ghost" size="sm"
                            className="ml-2 h-6 text-xs text-[#6D2C91] hover:text-[#6D2C91] px-1"
                            onClick={() => setChangingTarget(true)}
                            data-testid="distgroup-move-target-change">
                      change
                    </Button>
                  )}
                </>
              )}
            </DialogTitle>
            <DialogDescription>Pick distributors to move into this group. Their previous group assignment is replaced.</DialogDescription>
          </DialogHeader>
          {/* Search + select-all controls */}
          {(() => {
            const filter = (assignFilter || "").toLowerCase();
            const visible = distributors.filter((d) => {
              if (d.group_id === assigning?.group.id) return true; // keep member badge visible
              if (!filter) return true;
              return (d.name || "").toLowerCase().includes(filter)
                  || (d.code || "").toLowerCase().includes(filter);
            });
            const eligible = visible.filter((d) => d.group_id !== assigning?.group.id);
            const allEligibleSelected =
              eligible.length > 0 && eligible.every((d) => assigning?.selected?.has(d.id));
            return (
              <>
                <div className="flex flex-wrap items-center gap-2 pb-2">
                  <Input
                    value={assignFilter}
                    onChange={(e) => setAssignFilter(e.target.value)}
                    placeholder="Search by code or name…"
                    className="h-9 flex-1 min-w-[10rem]"
                    data-testid="dg-assign-filter"
                  />
                  <Button variant="outline" size="sm"
                          data-testid="dg-assign-select-visible"
                          disabled={eligible.length === 0}
                          onClick={() => {
                            const s = new Set(assigning.selected);
                            if (allEligibleSelected) {
                              eligible.forEach((d) => s.delete(d.id));
                            } else {
                              eligible.forEach((d) => s.add(d.id));
                            }
                            setAssigning({ ...assigning, selected: s });
                          }}>
                    {allEligibleSelected ? "Clear visible" : `Select ${eligible.length}`}
                  </Button>
                  <Badge variant="outline"
                          className="text-xs"
                          data-testid="distgroup-selected-count">
                    {(assigning?.selected?.size || 0)} selected
                  </Badge>
                </div>
                <div className="max-h-[55vh] overflow-y-auto border rounded-lg divide-y"
                      data-testid="dg-assign-list">
                  {visible.length === 0 && (
                    <div className="p-4 text-center text-sm text-neutral-500">
                      No distributors match "{assignFilter}".
                    </div>
                  )}
                  {visible.map((d) => {
                    const isMember = d.group_id === assigning?.group.id;
                    const isSelected = assigning?.selected?.has(d.id);
                    return (
                      <label key={d.id}
                              className={`flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer ${isMember ? "opacity-50" : ""}`}
                              data-testid={`dg-assign-row-${d.code}`}>
                        <input type="checkbox" checked={!!isSelected} disabled={isMember}
                                data-testid={`distgroup-multi-select-checkbox-${d.code}`}
                                onChange={(e) => {
                                  const s = new Set(assigning.selected);
                                  if (e.target.checked) s.add(d.id); else s.delete(d.id);
                                  setAssigning({ ...assigning, selected: s });
                                }} />
                        <div className="flex-1">
                          <div className="text-sm font-medium">{d.name}</div>
                          <div className="text-xs text-neutral-500">{d.code}</div>
                        </div>
                        {isMember && <Badge variant="outline" className="text-[10px]">Already in group</Badge>}
                      </label>
                    );
                  })}
                </div>
              </>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssigning(null); setAssignFilter(""); }}>Cancel</Button>
            <Button onClick={doAssign} disabled={!assigning?.selected?.size}
                    data-testid="distgroup-move-apply"
                    className="tulsi-gradient text-white font-semibold">
              Assign {assigning?.selected?.size || 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete group '{confirmDelete?.code}'?</AlertDialogTitle>
            <AlertDialogDescription>All assigned distributors must be reassigned first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
